/**
 * Body-free, locale-aware catalog index for the eager app shell.
 *
 * The full catalog ({@link ./localized.ts}) statically imports every lesson,
 * article, fundamental and build BODY in both locales. The global nav menus,
 * the footer and the search index only need titles and short descriptions, so
 * pulling the full catalog into the eager shell shipped ~11k lines of prose
 * (in both EN and DE) to every visitor on first paint.
 *
 * This module instead imports a pre-generated, body-free projection of the
 * catalog ({@link ./generated/catalog-index.json}, written by
 * `scripts/generate-catalog-index.ts` and pinned against the live catalog by
 * `./catalog-index.test.ts`). It carries zero body content, so nav-config,
 * nav-dialogs, the footer and search can link to every page without dragging a
 * single lesson body onto the critical path.
 *
 * Detail pages keep using {@link ./localized.ts} `getCatalog()` for the full
 * bodies they actually render.
 */
import type { Locale } from '../lib/i18n/config'
import type { CatalogIndex } from './catalog-index-projection'
// Server/test build: every locale statically. The browser build swaps this
// module for `catalog-index-source.client.ts` (English static, the other
// locales lazy + preloaded before hydration), see `clientOverrides` in
// vite.config.ts.
import { getCatalogIndexSource } from './catalog-index-source'

export type {
  CatalogIndex,
  CourseIndex,
  LessonIndex,
  ResourceIndex,
  ArticleIndex,
  FundamentalIndex,
  BuildIndex,
  GlossaryIndex,
  ComparisonIndex,
  GuideIndex,
  ToolIndex,
  ChangelogIndex,
  CommunityPostIndex,
} from './catalog-index-projection'

/** Return the slim, body-free catalog index for a locale. */
export function getCatalogIndex(locale: Locale): CatalogIndex {
  return getCatalogIndexSource(locale)
}
